import { useEffect, useState } from 'react';
import { History, Search } from 'lucide-react';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import ActionButton from '../../../components/Buttons/ActionButton';
import SearchInputField from '../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import FiscalEventService from '../../../services/fiscal/FiscalEventService';
import {
  FiscalEvent,
  FiscalEventStatus,
  FiscalEventType,
} from '../../../types/fiscal/FiscalEvent';
import FiscalEventsList from '../../../components/FiscalEvents/FiscalEventsList';
import FiscalEventDetailModal from '../../../components/FiscalEvents/FiscalEventDetailModal';
import useAlerts from '../../../hooks/useAlerts';

const EVENT_TYPE_OPTIONS = [
  { value: 'RETURN', label: 'Retorno' },
  { value: 'SPECIAL_OPERATIONS', label: 'Operaciones especiales' },
  { value: 'INVALIDATION', label: 'Invalidación' },
  { value: 'CONTINGENCY', label: 'Contingencia' },
];

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Borrador' },
  { value: 'VALIDATED', label: 'Validado' },
  { value: 'SIGNED', label: 'Firmado, pendiente de transmisión' },
  { value: 'SENDING', label: 'Transmitiendo' },
  { value: 'PROCESSED', label: 'Procesado' },
  { value: 'REJECTED', label: 'Rechazado' },
  { value: 'UNKNOWN', label: 'Estado desconocido' },
  { value: 'INVALIDATED', label: 'Evento invalidado' },
];

const DEFAULT_PAGE_SIZE = 20;

export default function FiscalEventsPage() {
  const [saleId, setSaleId] = useState('');
  const [eventType, setEventType] = useState<FiscalEventType | ''>('');
  const [status, setStatus] = useState<FiscalEventStatus | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [events, setEvents] = useState<FiscalEvent[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [detail, setDetail] = useState<FiscalEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const { show } = useAlerts();

  const search = async (targetPage = 0, size = pageSize) => {
    setLoading(true);
    try {
      const response = await FiscalEventService.findFiltered(
        {
          module: 'SALES',
          eventType: eventType || undefined,
          status: status || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          documentId: Number(saleId) || undefined,
        },
        targetPage,
        size
      );
      setEvents(response.data.content);
      setPage(response.data.number);
      setPageSize(response.data.size);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error: any) {
      show(
        'error',
        error?.response?.data?.title || 'Consulta fallida',
        error?.response?.data?.message ||
          'No se pudieron consultar los eventos fiscales.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void search(0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => void search(0, pageSize);
  const handlePageChange = (nextPage: number) =>
    void search(nextPage, pageSize);
  const handlePageSizeChange = (size: number) => void search(0, size);

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <main className="space-y-5 rounded-xl border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark sm:p-6">
        <header className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-3 text-primary">
            <History className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-black dark:text-white">
              Historial de Eventos Fiscales
            </h1>
            <p className="mt-1 text-sm text-body">
              Revise estados, observaciones, sellos y archivos originales de
              todos los tipos de evento.
            </p>
          </div>
        </header>

        <section className="grid gap-3 rounded-xl border border-stroke p-4 dark:border-strokedark sm:grid-cols-2 lg:grid-cols-5">
          <SearchInputField
            name="fiscal-event-sale"
            label="Venta asociada"
            ariaLabel="Identificador de venta"
            inputMode="numeric"
            value={saleId}
            onChange={(event) => setSaleId(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyFilters();
            }}
            placeholder="Todas"
            withMargin={false}
          />
          <SelectField
            label="Tipo de evento"
            name="fiscal-event-type"
            value={eventType}
            onChange={(event) =>
              setEventType(event.target.value as FiscalEventType)
            }
            options={EVENT_TYPE_OPTIONS}
            placeholder="Todos"
            withMargin={false}
            iconKey="list"
          />
          <SelectField
            label="Estado"
            name="fiscal-event-status"
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as FiscalEventStatus)
            }
            options={STATUS_OPTIONS}
            placeholder="Todos"
            withMargin={false}
            iconKey="status"
          />
          <DatePicker
            label="Desde"
            name="fiscal-event-start-date"
            value={startDate}
            onChange={setStartDate}
            withMargin={false}
            iconKey="calendar"
          />
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <DatePicker
                label="Hasta"
                name="fiscal-event-end-date"
                value={endDate}
                onChange={setEndDate}
                withMargin={false}
                iconKey="calendar"
              />
            </div>
            <ActionButton
              label="Filtrar"
              icon={<Search className="h-4 w-4" />}
              loading={loading}
              fullWidth={false}
              onClick={applyFilters}
            />
          </div>
        </section>

        <FiscalEventsList events={events} onOpen={setDetail} />
        <BasicPagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          size={pageSize}
          totalElements={totalElements}
          onSizeChange={handlePageSizeChange}
        />
      </main>
      <FiscalEventDetailModal event={detail} onClose={() => setDetail(null)} />
    </WorkspaceLayout>
  );
}
